// DTOs de "Entrada a Pits" (coordinador) — pedido Robotpack 2026-09.
//
// Indicador: promedio de minutos por EVENTO de paro para causales específicas
// configurables (ej. FO01 "Cambio de Rollo" meta 4 min, FO07 "Paro por cambio
// de referencia" meta 10 min). La meta es configurable: default por causal +
// override por línea (tabla `metas_entrada_pits`; una fila con linea_id NULL
// es la meta default y define que la causal PARTICIPA del indicador).
//
// Endpoints:
//   GET /api/coordinador/entrada-pits
//     ?desde&hasta&planta_id&area_id&linea_id&lider_user_id&turno_id&supervisor_user_id
//   GET /api/coordinador/configuracion/metas-pits
//   PUT /api/coordinador/configuracion/metas-pits
//
// Fórmula: SUM(minutos_perdidos) / COUNT(DISTINCT paro_evento_id) — un paro
// que cruza corte de SKU se fragmenta en varias filas de `registros_paro` con
// el mismo paro_evento_id (mig 078); contar filas duplicaría eventos. Solo
// paros CERRADOS (hora_fin IS NOT NULL) con padre estado='valido'.
//
// IMPORTANTE: el endpoint consulta `registros_paro` DIRECTO (join al padre
// registros_produccion_hora) — NUNCA las MVs de KPIs, que excluyen paros
// programados (mig 042) y FO01/FO07 son justamente programados en Robotpack.
import { z } from 'zod';
// =============================================================================
// Filtros
// =============================================================================
export const entradaPitsQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    planta_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
    lider_user_id: z.string().uuid().optional(),
    turno_id: z.string().uuid().optional(),
    supervisor_user_id: z.string().uuid().optional(),
});
// =============================================================================
// Recursos (dropdowns)
// =============================================================================
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    area_id: z.string().uuid(),
});
const turnoResumenSchema = z.object({
    id: z.string().uuid(),
    numero: z.number().int(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
/** Opción de dropdown de persona (líder o supervisor). */
const personaOpcionSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
});
/** Causal participante del indicador (tiene meta default configurada). */
export const causalPitsSchema = z.object({
    causal_id: z.string().uuid(),
    causal_codigo: z.string(),
    causal_nombre: z.string(),
    meta_minutos_default: z.number().positive(),
});
// =============================================================================
// Indicador por causal (KPI card)
// =============================================================================
export const pitsIndicadorSchema = z.object({
    causal_id: z.string().uuid(),
    causal_codigo: z.string(),
    causal_nombre: z.string(),
    meta_minutos_default: z.number().positive(),
    eventos_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    /** Promedio min/evento. Null si no hubo eventos en el rango. */
    promedio_minutos: z.number().min(0).nullable(),
    /** promedio <= meta default. Null si no hubo eventos. */
    cumple_meta: z.boolean().nullable(),
});
// =============================================================================
// Desglose por (causal × línea) — meta RESUELTA (override de línea o default)
// =============================================================================
export const pitsPorLineaSchema = z.object({
    causal_id: z.string().uuid(),
    causal_codigo: z.string(),
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    eventos_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    promedio_minutos: z.number().min(0),
    /** COALESCE(override de la línea, default de la causal). */
    meta_minutos: z.number().positive(),
    cumple_meta: z.boolean(),
});
// =============================================================================
// Desglose por (causal × persona) — líder o supervisor
// =============================================================================
export const pitsPorPersonaSchema = z.object({
    causal_id: z.string().uuid(),
    causal_codigo: z.string(),
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    eventos_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    promedio_minutos: z.number().min(0),
});
// =============================================================================
// Tendencia diaria por (fecha × causal)
// =============================================================================
export const pitsTendenciaSchema = z.object({
    fecha: z.string().date(),
    causal_id: z.string().uuid(),
    causal_codigo: z.string(),
    eventos_count: z.number().int().min(0),
    promedio_minutos: z.number().min(0),
});
// =============================================================================
// Historial de eventos (iteración 2 — validación de datos)
//
// Un row = UN paro_evento_id: los fragmentos multi-SKU (mig 078) se agrupan
// en el server (MIN(hora_inicio), MAX(hora_fin), SUM(minutos_perdidos)).
// El response viene capado a los EVENTOS_PITS_CAP más recientes; el filtrado
// por causal del desglose lo hace la UI client-side (decisión documentada en
// el response schema, campo `eventos`).
// =============================================================================
/** Cap de eventos del historial en el response (el total real va aparte). */
export const EVENTOS_PITS_CAP = 200;
export const pitsEventoSchema = z.object({
    paro_evento_id: z.string().uuid(),
    /** Fecha operativa del bloque padre. */
    fecha: z.string().date(),
    /** HH:MM hora de planta (timestamps fake-UTC del paro, leídos en UTC). */
    hora_inicio: z.string(),
    /** HH:MM hora de planta. Nunca null: el indicador solo mira paros cerrados. */
    hora_fin: z.string(),
    /** Minutos totales del evento (suma de fragmentos). */
    minutos: z.number().min(0),
    causal_id: z.string().uuid(),
    causal_nombre: z.string(),
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    lider_user_id: z.string().uuid().nullable(),
    /** Nombre completo del líder asignado del bloque. Null si no tenía. */
    lider_nombre: z.string().nullable(),
    turno_numero: z.number().int(),
    turno_nombre: z.string(),
    /** Supervisor PROGRAMADO de la (fecha, turno) — null si no había programación. */
    supervisor_user_id: z.string().uuid().nullable(),
    supervisor_nombre: z.string().nullable(),
    observaciones: z.string().nullable(),
});
// =============================================================================
// Response GET /api/coordinador/entrada-pits
// =============================================================================
export const coordinadorEntradaPitsSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(plantaResumenSchema),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        planta_id: z.string().uuid().nullable(),
        area_id: z.string().uuid().nullable(),
        linea_id: z.string().uuid().nullable(),
        lider_user_id: z.string().uuid().nullable(),
        turno_id: z.string().uuid().nullable(),
        supervisor_user_id: z.string().uuid().nullable(),
    }),
    recursos: z.object({
        plantas: z.array(plantaResumenSchema),
        areas: z.array(areaResumenSchema),
        lineas: z.array(lineaResumenSchema),
        turnos: z.array(turnoResumenSchema),
        /** Líderes con capturas en el rango/scope (alimenta el filtro). */
        lideres: z.array(personaOpcionSchema),
        /** Supervisores programados en el rango (patrón C7). */
        supervisores: z.array(personaOpcionSchema),
        /** Causales participantes (con meta default configurada). */
        causales: z.array(causalPitsSchema),
    }),
    /** KPI por causal participante. Ordenado por código de causal. */
    indicadores: z.array(pitsIndicadorSchema),
    /** Desglose (causal × línea) con meta resuelta. Solo líneas con eventos. */
    por_linea: z.array(pitsPorLineaSchema),
    /** Desglose (causal × líder). Solo líderes con eventos. */
    por_lider: z.array(pitsPorPersonaSchema),
    /** Desglose (causal × supervisor programado de la (fecha, turno)). */
    por_supervisor: z.array(pitsPorPersonaSchema),
    /** Serie diaria (fecha × causal). Ordenada por fecha ASC. */
    tendencia: z.array(pitsTendenciaSchema),
    /**
     * Historial de eventos (más reciente primero), capado a EVENTOS_PITS_CAP.
     * Respeta TODOS los filtros del query. NO se filtra por causal en el
     * server: incluye todas las causales participantes y la UI filtra
     * client-side por la causal activa del desglose — así cambiar de causal en
     * el tab es instantáneo (sin refetch) y los KPIs/gráficas (que muestran
     * todas las causales) comparten un único fetch con el historial.
     */
    eventos: z.array(pitsEventoSchema),
    /** Total real de eventos que matchean los filtros (para "mostrando X de N"). */
    eventos_total: z.number().int().min(0),
});
// =============================================================================
// Configuración — GET /api/coordinador/configuracion/metas-pits
// =============================================================================
/** Causal del catálogo (para agregar/quitar participantes). */
export const causalCatalogoPitsSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    /** Nombre de la causal madre (null si la causal es raíz). */
    causal_madre_nombre: z.string().nullable(),
});
/** Fila cruda de metas_entrada_pits (linea_id NULL = meta default). */
export const metaPitsRowSchema = z.object({
    causal_id: z.string().uuid(),
    linea_id: z.string().uuid().nullable(),
    meta_minutos: z.number().positive(),
});
export const coordinadorMetasPitsSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    /** Catálogo completo de causales activas del tenant. */
    causales: z.array(causalCatalogoPitsSchema),
    /** Líneas activas del scope del coordinador (para overrides). */
    lineas: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
    })),
    /** Metas actuales: defaults (linea_id NULL) + overrides por línea. */
    metas: z.array(metaPitsRowSchema),
});
// =============================================================================
// Configuración — PUT /api/coordinador/configuracion/metas-pits
//
// Semántica de RECONCILIACIÓN TOTAL: el body declara el estado deseado
// completo. Metas existentes que no vengan en el body se eliminan (es tabla
// de configuración pura, mismo criterio hard-delete que skus_lineas — el
// histórico del indicador no depende de estas filas).
// =============================================================================
export const metasPitsParticipanteSchema = z.object({
    causal_id: z.string().uuid(),
    /** Meta default (min/evento) — obligatoria: define la participación. */
    meta_minutos_default: z.number().positive().max(9999.99),
    /** Overrides por línea (opcional, solo líneas que difieren del default). */
    overrides: z
        .array(z.object({
        linea_id: z.string().uuid(),
        meta_minutos: z.number().positive().max(9999.99),
    }))
        .default([]),
});
export const metasPitsPutSchema = z.object({
    participantes: z
        .array(metasPitsParticipanteSchema)
        .max(50)
        .refine((arr) => new Set(arr.map((p) => p.causal_id)).size === arr.length, 'causal_id duplicado en participantes'),
});
export const metasPitsPutResultSchema = z.object({
    upserts: z.number().int().min(0),
    deletes: z.number().int().min(0),
});
//# sourceMappingURL=coordinador_entrada_pits.js.map